import { useEffect, useMemo, useState } from 'react';
import {
  SpaceBetween,
  Table,
  Header,
  Button,
  Box,
  TableProps,
  Popover
} from '@amzn/awsui-components-react';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import { Order, OrderItem, UpdateItemDetailsCommandInput } from '@amzn/ito-client';

import EmptyTableBox from 'common/EmptyTableBox/EmptyTableBox';
import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { ColumnSetting } from 'common/utils/config-models';
import { getString, buildColumnDefinitions, enhanceColumnDefinitionsForRowEditing, AlwaysEditableRowConfig, ActionConfigProps, TableActionsProps } from 'common';

import standardOrdersTableConfig from "./standard-items-requested.table-config.json";
import nonStandardOrdersTableConfig from "./nonstandard-items-requested.table-config.json";
import { AddSubLineItemModal } from './AddSubLineItemModal';
import { LineCommentModal } from './CommentsModal';
import { useUpdateItemDetails, usePostComment } from 'hooks';
import { CSRFService } from 'services/csrf-service';
import { setNestedProperty } from '../OrderSummaryTab/useOrderSummaryForm';
import { useOrderEditActions, useOrderEditItems } from 'views/OrderDetailsSingleViewPage/useOrderEdit';
import TableActions from './SubLineItemsTable/TableActions';
import { ItemsTableState, useItemsTableState } from 'hooks/useItemsTableState';
import { ChangeNoteModal } from './ChangeNoteModal/ChangeNoteModal';
import { UserPermissions } from 'services/user-permissions';
import { PERMISSIONS } from 'configuration/config';
import { HardwareOrderStatus } from 'interfaces/hardware-models';
import { STATUS_OPTIONS } from 'interfaces/hardware-status-options';
import { isItemCancellationAllowed, isLastNonCancelledItem } from '../../../../helpers/cancellation-helpers';
import { CancelOrderModal } from '../OrderSummaryTab/CancelOrderModal';
import { useCancelOrder } from '../../../../hooks/useCancelOrder';

const standardColumns: ColumnSetting[] = standardOrdersTableConfig.columns;
const nonStandardColumns: ColumnSetting[] = nonStandardOrdersTableConfig.columns;
const stringPath = "orderDetails.tabs.itemsRequested";

export interface ItemsRequestedTableProps {
  order: Order,
  amznhamOrder?: Order,
  items: OrderItem[],
  selectedItems: OrderItem[],
  useOrderEditItems: useOrderEditItems,
  flashBarItemsActions: useFlashBarItemsActions,
  useOrderActions: useOrderEditActions,
  onCommentsRefresh?: () => void,
}

const ItemsRequestedTable = (props: ItemsRequestedTableProps) => {
  const { state, updateState } = useItemsTableState();
  const { editedValues, savingRowId, currentItemChanges, currentItem, pendingNoteText, pendingToken, isWaitingForUpdateResult } = state;

  const enableEditQuantity = useMemo(() => UserPermissions.instance.hasPermission(PERMISSIONS.ENABLE_EDIT_QUANTITY_REQUESTED), []);
  const enableCancelOrder = useMemo(() => UserPermissions.instance.hasPermission(PERMISSIONS.SHOW_CANCEL_ORDER), []);

  // Cancel order modal state for last-item cancellation
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const { isSubmitting: isCancelOrderSubmitting, error: cancelOrderError, cancelOrder, data: cancelOrderData } = useCancelOrder();

  const getOriginalValues = (item: OrderItem, changedFields: Record<string, any>) => {
    const originalValues: Record<string, any> = {};
    Object.keys(changedFields).forEach(fieldPath => {
      const value = fieldPath.split('.').reduce((obj: any, key) => obj?.[key], item);
      originalValues[fieldPath] = value;
    });
    return originalValues;
  };

  // Build column definitions based on requestType
  const baseColumns = useMemo(() => {
    const columns = (props.order.details as any)["requestType"] === 'NonStandard' ? nonStandardColumns : standardColumns;
    return columns;
  }, [(props.order.details as any)["requestType"], props.useOrderEditItems.canBeEdited]);

  const baseColumnDefinitions = useMemo(() => {
    return buildColumnDefinitions(baseColumns, undefined, props.useOrderEditItems.canBeEdited);
  }, [baseColumns, props.useOrderEditItems.canBeEdited]);

  // Actions
  const { data, error, doUpdateItemDetailsAsync } = useUpdateItemDetails();
  const { doPostComment } = usePostComment();
  const onSelectionChange: NonCancelableEventHandler<TableProps.SelectionChangeDetail<any>> = e => {
    props.useOrderActions.setSelectedLineItems(e.detail.selectedItems);
  };

  const handleFieldChange = (itemId: string, fieldName: string, value: any) => {
    updateState('editedValues', {
      ...editedValues,
      [itemId]: {
        ...editedValues[itemId],
        [fieldName]: value
      }
    });
  };

  const handleSave = async (item: OrderItem) => {
    if (!item.itemId) return;

    const itemEditedValues = editedValues[item.itemId] || {};
    if (Object.keys(itemEditedValues).length === 0) return;

    if (!enableEditQuantity) {
      // This function should not be called for users without permission
      // Original cell-level editing uses the table's built-in submit mechanism
      return;
    }

    // Filter out changes where new value equals original value
    const actualChanges = Object.fromEntries(
      Object.entries(itemEditedValues).filter(([fieldPath, newValue]) => {
        const originalValue = fieldPath.split('.').reduce((obj: any, key) => obj?.[key], item);
        return originalValue !== newValue;
      })
    );

    // If no actual changes after filtering, return early
    if (Object.keys(actualChanges).length === 0) return;

    // Validate business rules
    const validationErrors = validateBusinessRules(item, actualChanges);
    if (validationErrors.length > 0) {
      props.flashBarItemsActions.addErrorFlashBarItem(validationErrors.join(', '));
      return;
    }

    const hasOnlyStatusChange = 'status' in actualChanges && Object.keys(actualChanges).length === 1;

    updateState('savingRowId', item.itemId);

    if (hasOnlyStatusChange) {
      // HOP validation for item cancellation
      if (enableCancelOrder && actualChanges.status === HardwareOrderStatus.CANCELLED) {
        const orderForHopCheck = props.amznhamOrder ?? props.order;
        if (!isItemCancellationAllowed(orderForHopCheck, item.itemId!)) {
          props.flashBarItemsActions.addErrorFlashBarItem("Item cannot be cancelled — HOP order has progressed beyond Ready for Export.");
          updateState('savingRowId', undefined);
          return;
        }
        if (isLastNonCancelledItem(props.order, item.itemId!)) {
          setShowCancelOrderModal(true);
          updateState('savingRowId', undefined);
          return;
        }
      }

      // For status-only changes, update directly without modal
      try {

        const updateProps: UpdateItemDetailsCommandInput = {
          orderId: props.order.orderId,
          itemId: item.itemId
        };

        for (const [fieldName, value] of Object.entries(actualChanges)) {
          setNestedProperty(updateProps, fieldName, value);
        }

        await submitItemUpdate(doUpdateItemDetailsAsync, updateProps);

        const newEditedValues = { ...editedValues };
        delete newEditedValues[item.itemId];
        updateState('editedValues', newEditedValues);
        updateState('savingRowId', undefined);

        // Refresh comments in MessagesTab after a small delay to ensure the new comment is present
        setTimeout(() => {
          props.onCommentsRefresh?.();
        }, 800);

      } catch (updateError) {
        props.flashBarItemsActions.addErrorFlashBarItem(`${getString('banners.errors.errorUpdatingItem', stringPath)}: ${updateError instanceof Error ? updateError.message : String(updateError)}`);
        updateState('savingRowId', undefined);
      }
    } else {
      // For quantity changes or other changes, show modal for comment
      updateState('currentItemChanges', actualChanges);
      updateState('currentItem', item);
      setShowChangeNoteModal(true);
    }
  };

  // Map items to have id property for table-utils compatibility
  const itemsWithId = useMemo(() =>
    props.items.map(item => ({ ...item, id: item.itemId })),
    [props.items]
  );

  // Create stable selected items that match current data
  const selectedItemsWithCurrentData = useMemo(() => {
    if (props.selectedItems.length === 0) return [];
    const selectedItemIds = props.selectedItems.map(item => item.itemId);
    return itemsWithId.filter(item => selectedItemIds.includes(item.itemId));
  }, [itemsWithId, props.selectedItems]);

  const rowEditConfig: AlwaysEditableRowConfig = {
    editedValues,
    handleFieldChange
  };

  const ItemTableActions: React.ComponentType<TableActionsProps> = ({ item, savingRowId: currentSavingRowId, onSave }) => {
    if (!enableEditQuantity) return null;

    const itemEditedValues = item.itemId ? editedValues[item.itemId] : undefined;
    const itemHasChanges = !!(itemEditedValues && Object.keys(itemEditedValues).length > 0 &&
      Object.entries(itemEditedValues).some(([fieldPath, newValue]) => {
        const originalValue = fieldPath.split('.').reduce((obj: any, key) => obj?.[key], item);
        return originalValue !== newValue;
      }));

    return (
      <TableActions
        item={item}
        isEditing={false}
        hasChanges={itemHasChanges}
        savingRowId={currentSavingRowId}
        onSave={onSave}
        mode="saveOnly"
        testIdPart="items"
      />
    );
  };

  const actionConfig: ActionConfigProps = {
    savingRowId,
    TableActions: ItemTableActions,
    editMode: enableEditQuantity ? 'alwaysEditable' : 'byRow',
    onSaveWithItem: handleSave
  };

  const columnDefinitions = useMemo(() => {
    if (!props.useOrderEditItems.canBeEdited) {
      return baseColumnDefinitions;
    }

    if (enableEditQuantity) {
      const allowedStatuses = [HardwareOrderStatus.NEW, HardwareOrderStatus.ASSIGNED, HardwareOrderStatus.WORK_IN_PROGRESS, HardwareOrderStatus.PENDING_REQ_INFO, HardwareOrderStatus.PENDING_QUOTE];

      // Modify handleFieldChange to prevent quantity changes for disallowed statuses
      const modifiedRowEditConfig = {
        ...rowEditConfig,
        handleFieldChange: (itemId: string, fieldName: string, value: any) => {
          if (fieldName === 'quantity') {
            const item = props.items.find(i => i.itemId === itemId);
            if (item?.status && !allowedStatuses.includes(item.status as HardwareOrderStatus)) {
              return; // Don't allow changes
            }
          }
          handleFieldChange(itemId, fieldName, value);
        }
      };

      const enhancedColumns = enhanceColumnDefinitionsForRowEditing(
        baseColumns,
        baseColumnDefinitions,
        modifiedRowEditConfig,
        actionConfig
      );

      // Add visual disabled styling to quantity column for disallowed statuses
      return enhancedColumns.map(col => {
        if (col.id === 'quantity') {
          return {
            ...col,
            cell: (item: OrderItem) => {
              const isQuantityEditable = !item.status || allowedStatuses.includes(item.status as HardwareOrderStatus);
              const originalCell = col.cell;

              if (!isQuantityEditable && typeof originalCell === 'function') {
                const statusOption = STATUS_OPTIONS
                  .flatMap(group => group.options)
                  .find(option => option.value === item.status);
                const statusLabel = statusOption?.label || item.status;

                return (
                  <Popover
                    data-testid='quantity-disabled-tooltip'
                    content={`${getString('quantityDisabledTooltip', stringPath)} ${statusLabel}`}
                  >
                    <div style={{ opacity: 0.5, pointerEvents: 'none', color: '#687078' }}>
                      {originalCell(item)}
                    </div>
                  </Popover>
                );
              }

              return typeof originalCell === 'function' ? originalCell(item) : originalCell;
            }
          };
        }
        return col;
      });
    }

    // Return base columns with original cell-level editing but disable quantity column
    return baseColumnDefinitions.map(col => {
      if (col.id === 'quantity') {
        return { ...col, editConfig: undefined };
      }
      return col;
    });
  }, [baseColumns, baseColumnDefinitions, editedValues, savingRowId, props.useOrderEditItems.canBeEdited, enableEditQuantity, props.items, handleFieldChange]);

  // Add Subline item Button
  const [showSubLineItemModal, setShowSubLineItemModal] = useState(false);
  const disableSubLineButton = props.selectedItems.length !== 1; // Only one is selected to add sub line item

  const onAddSubLineItemClick = () => {
    setShowSubLineItemModal(true);
  }
  const onAddSubLineItemDismiss = () => {
    setShowSubLineItemModal(false);
  };

  const onChangeNoteModalDismiss = () => {
    setShowChangeNoteModal(false);
    updateState('savingRowId', undefined);
  };

  const submitChangeNote = async (noteText: string, token: string) => {
    try {
      await doPostComment(props.order.orderId!, noteText, token, []);
      props.flashBarItemsActions.addSuccessFlashBarItem(getString('banners.success.commentAdded', stringPath));
      // Refresh comments in MessagesTab after a small delay to ensure the new comment is included
      setTimeout(() => {
        props.onCommentsRefresh?.();
      }, 500);
    } catch (noteError) {
      // Show error message for note posting
      props.flashBarItemsActions.addErrorFlashBarItem(`${getString('banners.errors.errorAddingComment', stringPath)}: ${noteError instanceof Error ? noteError.message : String(noteError)}`);
    }
  }

  const handleRowSubmit = async (noteText: string) => {
    if (!currentItem?.itemId || !currentItemChanges || !enableEditQuantity) return;

    try {
      const updateProps: UpdateItemDetailsCommandInput = {
        orderId: props.order.orderId,
        itemId: currentItem.itemId
      };

      for (const [fieldName, value] of Object.entries(currentItemChanges)) {
        setNestedProperty(updateProps, fieldName, value);
      }

      const token = await submitItemUpdate(doUpdateItemDetailsAsync, updateProps);

      // Store the note text and token for later use
      updateState('pendingNoteText', noteText);
      updateState('pendingToken', token);
      updateState('isWaitingForUpdateResult', true);

    } catch (updateError) {
      // Show error message for any other errors
      props.flashBarItemsActions.addErrorFlashBarItem(`${getString('banners.errors.errorUpdatingItem', stringPath)}: ${updateError instanceof Error ? updateError.message : String(updateError)}`);
      // Clean up pending state
      cleanPendingState(updateState);
    }
  };

  // Add Comment Button
  const [showLineCommentModal, setShowLineCommentModal] = useState(false);
  const [showChangeNoteModal, setShowChangeNoteModal] = useState(false);
  const disableLineCommentButton = props.selectedItems.length <= 0; // One or more are selected to allow bulk line comment

  const onAddLineCommentClick = () => {
    setShowLineCommentModal(true);
  }
  const onAddLineCommentDismiss = () => {
    setShowLineCommentModal(false);
  };

  // Update subline items
  useEffect(() => {
    if (props.selectedItems.length === 0) {
      props.useOrderActions.updateSubLineItemSelected(undefined, undefined);
    }
    else if (props.selectedItems.length >= 1 && props.selectedItems[props.selectedItems.length - 1]) {
      const lastLineItemSelected = props.selectedItems[props.selectedItems.length - 1];
      props.useOrderActions.updateSubLineItemSelected!(props.order, lastLineItemSelected);
    }
  }, [props.selectedItems]);

  // Handle change note submit
  useEffect(() => {
    if (isWaitingForUpdateResult && pendingNoteText && pendingToken && !error) {
      const processSuccessfulUpdate = async () => {
        if (enableEditQuantity) {
          await submitChangeNote(pendingNoteText, pendingToken);
        }
        const newEditedValues = { ...editedValues };
        if (currentItem?.itemId) {
          delete newEditedValues[currentItem.itemId];
        }
        updateState('editedValues', newEditedValues);
        setShowChangeNoteModal(false);
        // Clean up pending state
        cleanPendingState(updateState);
      };
      processSuccessfulUpdate();
    }
  }, [isWaitingForUpdateResult, pendingNoteText, pendingToken, currentItem]);

  // Handle update result
  useEffect(() => {
    if (data && !error) {
      props.useOrderActions.flagOrderWithFirstEdit();
      props.useOrderActions.reloadOrder(props.order.orderId);;
      props.flashBarItemsActions.addSuccessFlashBarItem(getString('banners.success.itemUpdated', stringPath));
    } else if (error) {
      props.flashBarItemsActions.addErrorFlashBarItem(`${getString('banners.errors.errorUpdatingItem', stringPath)}: ${error.message}`);

      if (isWaitingForUpdateResult) {
        // Clean up pending state
        cleanPendingState(updateState);
      } else {
        setShowChangeNoteModal(false);
      }
    }
  }, [data, error]);

  useEffect(() => {
    const overDeliveredItems = props.items.some(item =>
      item.status !== 'CANCELLED' &&
      item.subLineItems?.some(subLineItem =>
        (subLineItem.quantityDelivered ?? 0) > (item.quantity ?? 0)
      )
    );
    if (overDeliveredItems) {
      props.flashBarItemsActions.addWarningFlashBarItem(getString('banners.warnings.overDeliveredItems', stringPath));
    }
  }, [props.items]);

  const handleSubmit = async (currentItem: OrderItem, columnDefinition: any, newValue: any) => {
    // Remove "$.", "$.name" --> "name";
    const pathOfFieldToUpdate = columnDefinition.selector.slice(2) as keyof UpdateItemDetailsCommandInput;

    // HOP validation for item cancellation via cell-level editing (behind feature flag)
    if (enableCancelOrder && pathOfFieldToUpdate === 'status' && newValue === HardwareOrderStatus.CANCELLED) {
      const orderForHopCheck = props.amznhamOrder ?? props.order;
      if (!isItemCancellationAllowed(orderForHopCheck, currentItem.itemId!)) {
        props.flashBarItemsActions.addErrorFlashBarItem("Item cannot be cancelled — HOP order has progressed beyond Ready for Export.");
        return;
      }
      if (isLastNonCancelledItem(props.order, currentItem.itemId!)) {
        setShowCancelOrderModal(true);
        return;
      }
    }

    const updateProps: UpdateItemDetailsCommandInput = {
      orderId: props.order.orderId,
      itemId: currentItem.itemId
    };

    setNestedProperty(updateProps, pathOfFieldToUpdate, newValue);

    await submitItemUpdate(doUpdateItemDetailsAsync, updateProps);
  }

  const validateBusinessRules = (item: OrderItem, changes: Record<string, any>): string[] => {
    const errors: string[] = [];

    if (changes.quantity !== undefined) {
      const quantity = Number(changes.quantity);
      if (isNaN(quantity) || quantity <= 0) {
        errors.push(getString('banners.errors.quantityReqValidation', stringPath));
      }

      // Only validate quantity restrictions if the quantity has actually changed
      const quantityHasChanged = Number(item.quantity) !== Number(changes.quantity);

      if (quantityHasChanged) {
        // Validate that quantity can only be changed if item status allows it
        const allowedStatuses = [HardwareOrderStatus.NEW, HardwareOrderStatus.ASSIGNED, HardwareOrderStatus.WORK_IN_PROGRESS, HardwareOrderStatus.PENDING_REQ_INFO, HardwareOrderStatus.PENDING_QUOTE];
        if (item.status && !allowedStatuses.includes(item.status as HardwareOrderStatus)) {
          const statusOption = STATUS_OPTIONS
            .flatMap(group => group.options)
            .find(option => option.value === item.status);

          const statusLabel = statusOption?.label || item.status;
          errors.push(`${getString('banners.errors.quantityReqAllowedStatus', stringPath)} ${statusLabel}`);
        }

        if (changes.status && !allowedStatuses.includes(changes.status as HardwareOrderStatus)) {
          const statusOption = STATUS_OPTIONS
            .flatMap(group => group.options)
            .find(option => option.value === changes.status);

          const statusLabel = statusOption?.label || changes.status;
          errors.push(`${getString('banners.errors.quantityReqAllowedNewStatus', stringPath)} ${statusLabel}`);
        }
      }
    }

    return errors;
  };

  const cleanPendingState = (updateState: (field: keyof ItemsTableState, value: any) => void) => {
    updateState('isWaitingForUpdateResult', false);
    updateState('pendingNoteText', undefined);
    updateState('pendingToken', undefined);
    updateState('savingRowId', undefined);
  }

  const submitItemUpdate = async (doUpdateItemDetailsAsync: (props: UpdateItemDetailsCommandInput, csrfToken: string) => Promise<void>, updateProps: UpdateItemDetailsCommandInput) => {
    const csrfService = new CSRFService();
    const token = await csrfService.getToken();

    await doUpdateItemDetailsAsync(updateProps, token);

    return token;
  }

  // Handle cancel order modal confirm (last-item cancellation triggers full order cancellation)
  const handleCancelOrderConfirm = async (reason: string) => {
    const csrfService = new CSRFService();
    const token = await csrfService.getToken();
    cancelOrder(props.order, reason, token, props.amznhamOrder);
  };

  // React to successful order cancellation submission
  useEffect(() => {
    if (cancelOrderData && !cancelOrderError) {
      props.flashBarItemsActions.addSuccessFlashBarItem("Cancellation has been initiated successfully.");
      setShowCancelOrderModal(false);
      props.useOrderActions.reloadOrder(props.order.orderId);
    }
  }, [cancelOrderData]);

  // React to order cancellation errors
  useEffect(() => {
    if (cancelOrderError) {
      props.flashBarItemsActions.addErrorFlashBarItem(cancelOrderError.message);
      setShowCancelOrderModal(false);
    }
  }, [cancelOrderError]);

  return (
    <Box>
      {showSubLineItemModal && (
        <AddSubLineItemModal
          showModal={showSubLineItemModal}
          order={props.order}
          item={props.selectedItems[0]}
          flashBarItemsActions={props.flashBarItemsActions}
          useOrderEditActions={props.useOrderActions}
          onDismiss={onAddSubLineItemDismiss} />
      )}

      {showLineCommentModal && (
        <LineCommentModal
          items={props.selectedItems}
          showComments={props.selectedItems.length == 1}
          showModal={showLineCommentModal}
          useOrderEditActions={props.useOrderActions}
          onDismiss={onAddLineCommentDismiss} />
      )}

      {showChangeNoteModal && enableEditQuantity && (
        <ChangeNoteModal
          showModal={showChangeNoteModal}
          onDismiss={onChangeNoteModalDismiss}
          changes={currentItemChanges}
          columnDefinitions={baseColumnDefinitions}
          originalValues={currentItem && currentItemChanges ? getOriginalValues(currentItem, currentItemChanges) : undefined}
          username={UserPermissions.instance.username}
          order={props.order}
          item={currentItem}
          onSubmit={handleRowSubmit}
        />
      )}

      <CancelOrderModal
        visible={showCancelOrderModal}
        order={props.order}
        onDismiss={() => {
          setShowCancelOrderModal(false);
        }}
        onConfirm={handleCancelOrderConfirm}
        isSubmitting={isCancelOrderSubmitting}
      />

      <Table
        data-testid="items-requested-table"
        submitEdit={handleSubmit}
        items={itemsWithId}
        columnDefinitions={columnDefinitions}
        selectedItems={selectedItemsWithCurrentData}
        onSelectionChange={onSelectionChange}
        resizableColumns={true}
        empty={<EmptyTableBox />}
        loadingText={getString('table.loadingText', stringPath)}
        selectionType="multi"
        variant="embedded"
        wrapLines
        contentDensity="comfortable"
        header={
          <Header
            data-testid="items-requested-table-header"
            variant="awsui-h1-sticky"
            counter={`(${props.items.length})`}
            description={getString(`${stringPath}.table.headerDescription`)}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {!props.useOrderEditItems.isReadOnly && <Button
                  data-testid="item-add-linecomment-button"
                  onClick={onAddLineCommentClick}
                  disabled={disableLineCommentButton}
                >
                  {props.selectedItems.length < 2 ? getString(`${stringPath}.table.viewCommentButton`) : getString(`${stringPath}.table.addLineCommentButton`)}
                </Button>}
                {props.useOrderEditItems.canBeEdited && <Button
                  data-testid="item-add-sublineitem-button"
                  variant="primary"
                  onClick={onAddSubLineItemClick}
                  disabled={disableSubLineButton}
                >
                  {getString(`${stringPath}.table.addSubLineItemButton`)}
                </Button>}
              </SpaceBetween>
            }
          >
            {getString(`${stringPath}.table.headerTitle`)}
          </Header>
        }
      />
    </Box>
  );
}

export default ItemsRequestedTable;
